/**
 * Add-on Variant Sync
 * Automatically updates Selleasy add-on variant selections based on main product variant changes
 */

window.FoxTheme = window.FoxTheme || {};

FoxTheme.AddonVariantSync = (function() {
  'use strict';

  // Configuration: Main product variant display value -> Add-on option value mapping
  const VARIANT_MAPPINGS = {
    '146x140 cm': '51955121226033',
    '166x160 cm': '51955121258801',
    '70x140 cm': '51955121226033',
    '80x160 cm': '51955121258801',
  };

  // Selectors
  const ADDON_SELECTOR = '.lb-variant-picker';
  const VARIANT_LABEL_SELECTOR = 'variant-selects label';

  let initialized = false;
  let mutationObserver = null;
  let variantChangeUnsubscriber = null;
  let optionChangeUnsubscriber = null;

  /**
   * Initialize the add-on variant sync functionality
   */
  function init() {
    if (initialized) return;
    
    
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
      return;
    }

    // Subscribe to variant change events
    subscribeToVariantEvents();
    
    // Start observing for add-on selector appearance
    observeForAddonSelector();
    
    // Check if add-on selector already exists
    const existingAddonSelector = document.querySelector(ADDON_SELECTOR);
    if (existingAddonSelector) {
      syncCurrentVariant();
    }

    initialized = true;
  }

  /**
   * Subscribe to theme's variant change events
   */
  function subscribeToVariantEvents() {
    // Subscribe to main variant change events
    if (window.FoxTheme && FoxTheme.pubsub) {
      variantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
        handleVariantChange
      );

      optionChangeUnsubscriber = FoxTheme.pubsub.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
        handleOptionChange
      );

    } else {
      observeVariantLabels();
    }
  }

  /**
   * Handle variant change events from the theme
   */
  function handleVariantChange(event) {
    
    // Small delay to ensure DOM is updated
    setTimeout(() => {
      syncCurrentVariant();
    }, 100);
  }

  /**
   * Handle option value selection change events
   */
  function handleOptionChange(event) {
    
    // Small delay to ensure DOM is updated
    setTimeout(() => {
      syncCurrentVariant();
    }, 100);
  }

  /**
   * Observe for the add-on selector to appear in the DOM
   */
  function observeForAddonSelector() {
    if (mutationObserver) {
      mutationObserver.disconnect();
    }

    mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          for (const node of mutation.addedNodes) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check if the added node contains the add-on selector
              const addonSelector = node.matches && node.matches(ADDON_SELECTOR) 
                ? node 
                : node.querySelector && node.querySelector(ADDON_SELECTOR);
              
              if (addonSelector) {
                syncCurrentVariant();
                return;
              }
            }
          }
        }
      }
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Observe variant labels for changes (fallback method)
   */
  function observeVariantLabels() {
    const variantContainer = document.querySelector('variant-selects');
    if (!variantContainer) return;

    const labelObserver = new MutationObserver(() => {
      syncCurrentVariant();
    });

    labelObserver.observe(variantContainer, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  /**
   * Get the currently selected variant display value
   */
  function getCurrentVariantValue() {
    // Look for checked radio inputs or selected options in variant selects
    const variantSelects = document.querySelector('variant-selects');
    if (!variantSelects) return null;

    // Check for radio button selections
    const checkedInput = variantSelects.querySelector('input[type="radio"]:checked');
    if (checkedInput) {
      const label = variantSelects.querySelector(`label[for="${checkedInput.id}"]`);
      if (label) {
        // Extract the variant value, removing any unavailable text
        const labelText = label.textContent || label.innerText;
        const cleanText = labelText.replace(/\s*\(.*?\)\s*$/, '').trim(); // Remove (unavailable) etc.
        return cleanText;
      }
    }

    // Check for dropdown selections
    const selectElement = variantSelects.querySelector('select');
    if (selectElement && selectElement.selectedOptions.length > 0) {
      const selectedOption = selectElement.selectedOptions[0];
      return selectedOption.textContent.trim();
    }

    return null;
  }

  /**
   * Sync the current variant with the add-on selector
   */
  function syncCurrentVariant() {
    const addonSelector = document.querySelector(ADDON_SELECTOR);
    if (!addonSelector) {
      return;
    }

    const currentVariant = getCurrentVariantValue();
    if (!currentVariant) {
      return;
    }


    // Check if we have a mapping for this variant
    const mappedValue = VARIANT_MAPPINGS[currentVariant];
    if (!mappedValue) {
      return;
    }

    // Check if the mapped option exists in the add-on selector
    const targetOption = addonSelector.querySelector(`option[value="${mappedValue}"]`);
    if (!targetOption) {
      return;
    }

    // Only update if the value is different
    if (addonSelector.value !== mappedValue) {
      
      addonSelector.value = mappedValue;
      
      // Dispatch change event to notify the add-on app
      const changeEvent = new Event('change', { bubbles: true, cancelable: true });
      addonSelector.dispatchEvent(changeEvent);
      
    } else {
    }
  }

  /**
   * Add a new variant mapping
   * @param {string} variantValue - The main product variant display value
   * @param {string} addonOptionValue - The add-on option value to select
   */
  function addMapping(variantValue, addonOptionValue) {
    VARIANT_MAPPINGS[variantValue] = addonOptionValue;
    
    // Sync immediately if the current variant matches the new mapping
    const currentVariant = getCurrentVariantValue();
    if (currentVariant === variantValue) {
      syncCurrentVariant();
    }
  }

  /**
   * Remove a variant mapping
   * @param {string} variantValue - The main product variant display value to remove
   */
  function removeMapping(variantValue) {
    if (VARIANT_MAPPINGS[variantValue]) {
      delete VARIANT_MAPPINGS[variantValue];
    }
  }

  /**
   * Get all current mappings
   */
  function getMappings() {
    return { ...VARIANT_MAPPINGS };
  }

  /**
   * Cleanup function
   */
  function destroy() {
    if (mutationObserver) {
      mutationObserver.disconnect();
      mutationObserver = null;
    }

    if (variantChangeUnsubscriber) {
      variantChangeUnsubscriber();
      variantChangeUnsubscriber = null;
    }

    if (optionChangeUnsubscriber) {
      optionChangeUnsubscriber();
      optionChangeUnsubscriber = null;
    }

    initialized = false;
  }

  // Public API
  return {
    init,
    addMapping,
    removeMapping,
    getMappings,
    destroy,
    syncCurrentVariant
  };
})();

// Auto-initialize when the script loads
FoxTheme.DOMready(() => {
  FoxTheme.AddonVariantSync.init();
});
